<script setup>
import { computed, onMounted, ref } from 'vue'
import { useCanvasStore } from '@/store/canvas'
import CanvasRenderer from '@/components/CanvasRenderer.vue'
import { DateTime } from 'luxon'
import { canViewDeadAnimalsHelper, canViewHeatmap } from '@/composables/permissionHelper'
import { useInstanceStore } from '@/store/backendInstances'
import { useDisplay } from 'vuetify'
import { humanReadableTimestamp } from '@/composables/datetime'

const instanceStore = useInstanceStore()
const canvasStore = useCanvasStore()
const { mobile } = useDisplay()

const props = defineProps({
  timestamp: { type: Number, default: null },
  canvasId: { type: String, default: null },
  presetImageSource: { type: String, default: 'stillFrame' },
})

const timestampToUse = computed(() => {
  if (props.timestamp) {
    return DateTime.fromMillis(props.timestamp)
  }
  return DateTime.now().startOf('second')
})

const elementProvider = ref(props.presetImageSource)
const pluginConfigForDeadAnimals = ref({})

const isStillFrame = computed(() => elementProvider.value === 'stillFrame')

const isPluginConfigEmpty = computed(() => {
  return Object.keys(pluginConfigForDeadAnimals.value).length === 0
})

const baseCanvas = computed(() => {
  return canvasStore.getBaseCanvas()
})

const setElementProvider = () => {
  if (isStillFrame.value) {
    elementProvider.value = 'artifact.heatmap'
    return
  }

  elementProvider.value = 'stillFrame'
}

const setPluginConfig = () => {
  if (!isPluginConfigEmpty.value) {
    pluginConfigForDeadAnimals.value = {}
    return
  }

  pluginConfigForDeadAnimals.value = {
    deadAnimals: {
      enabled: true,
      markerType: 'circle',
      hideIncidentOverlay: true,
    },
  }
}

onMounted(async () => {
  canvasStore.keepBaseCanvasLoaded()
})
</script>

<template>
  <v-hover v-slot="{ isHovering, props: hoverPropsBarnManagement }">
    <div class="d-flex justify-space-between align-stretch">
      <router-link
        class="text-truncate link"
        :to="{ name: 'CameraCanvas' }"
      >
        <v-card-title
          v-bind="hoverPropsBarnManagement"
          class="d-inline-flex align-center ga-2 cursor-pointer"
          style="max-width: 100%"
        >
          <v-icon
            icon="mdi-artboard"
            size="small"
          />
          <span
            class="text-truncate"
            :class="{ 'text-decoration-underline': isHovering }"
          >
            {{ $t('dashboards.titles.canvas_with_timestamp', { timestamp: humanReadableTimestamp(timestampToUse.toMillis(), false) }) }}
          </span>
          <v-icon
            class="redirect-arrow"
            :class="{ 'redirect-arrow-hovered': isHovering }"
            icon="mdi-arrow-right-thin"
            size="small"
          />
        </v-card-title>
      </router-link>
      <div class="pr-4 d-flex align-center ga-2">
        <v-btn
          v-if="canViewHeatmap(instanceStore.selectedInstanceId)"
          class="h-100 rounded-b-0"
          icon
          size="small"
          variant="elevated"
          :color="isStillFrame ? 'default' : 'success'"
          @click="setElementProvider"
        >
          <v-icon icon="mdi-image" />
          <v-tooltip
            activator="parent"
            location="top"
            scroll-strategy="close"
          >
            {{ $t(`canvas.settings.image.${isStillFrame ? 'switch_to_heatmap' : 'switch_to_stillframe'}`) }}
          </v-tooltip>
        </v-btn>
        <v-btn
          v-if="canViewDeadAnimalsHelper(instanceStore.selectedInstanceId)"
          class="h-100 rounded-b-0"
          icon
          size="small"
          variant="elevated"
          :color="isPluginConfigEmpty ? 'default' : 'primary'"
          @click="setPluginConfig"
        >
          <v-icon icon="mdi-skull-crossbones" />
          <v-tooltip
            activator="parent"
            location="top"
            scroll-strategy="close"
          >
            {{ $t(`canvas.settings.overlay.dead_animals.${isPluginConfigEmpty ? 'enable' : 'disable'}`) }}
          </v-tooltip>
        </v-btn>
      </div>
    </div>
  </v-hover>

  <v-glass-card
    v-if="baseCanvas"
    class="rounded-lg pa-0"
  >
    <CanvasRenderer
      v-model:element-provider="elementProvider"
      v-model:plugin-config="pluginConfigForDeadAnimals"
      :disable-zoom-buttons="true"
      :canvas-id="canvasId"
      :model-value:base-rotation="0"
      :disable-element-picker="true"
      :disable-time-jumper="true"
      :disable-navigation="true"
      :draggable="!mobile"
      :scrollable="false"
      :ctrl-scrollable="!mobile"
      :disable-dead-animal-overlay="true"
      :disable-regions-overlay="true"
      :disable-hide-ui-button="true"
      :shrink-all-overlays-by-default="true"
      :timestamp="timestampToUse"
    />
  </v-glass-card>
  <v-skeleton-loader
    v-else
    width="100%"
    class="rounded-lg"
    type="card"
  />
</template>
